import { useEffect, useState, type ReactNode } from "react";
import { NavLink, Outlet, useLocation } from "react-router";
import { Home, Inbox, CheckSquare, CalendarDays, Kanban, Building2, Activity, Ticket, FileText, Users, BookOpen, Shield, Search, MoreHorizontal, X, Settings, UserPlus } from "lucide-react";
import { useMe } from "./MeContext";
import { CommandK } from "./CommandK";
import { useQuery, invalidate } from "@/lib/useQuery";
import { api } from "@/lib/api";
import { ToastHost } from "@/components/ui";

interface Badges { inboxUnread: number; todosOpen: number; ticketsOpen: number; systemsRed: number; leadsPending: number; inboxes: { id: string; address: string; unread: number }[] }

function Item({ to, icon, label, count, dot, end }: { to: string; icon: ReactNode; label: string; count?: number; dot?: boolean; end?: boolean }) {
  return (
    <NavLink to={to} end={end} className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
      {icon}
      {label}
      {dot ? <span className="dot pulse" aria-label="A system is down" /> : count ? <span className={`count ${label === "Inbox" ? "bright" : ""}`}>{count}</span> : null}
    </NavLink>
  );
}

export function Shell() {
  const me = useMe();
  const [cmdk, setCmdk] = useState(false);
  const [more, setMore] = useState(false);
  const loc = useLocation();
  const { data: badges } = useQuery<Badges>("badges", () => api.get<Badges>("/badges"), []);
  useEffect(() => {
    // Mail arrives by webhook; the UI just has to look again. Every 45 s and whenever the tab comes back.
    const tick = () => { if (document.visibilityState === "visible") { invalidate("badges"); invalidate("threads"); invalidate("board"); invalidate("leads"); } };
    const id = window.setInterval(tick, 45000);
    document.addEventListener("visibilitychange", tick);
    window.addEventListener("focus", tick);
    return () => { window.clearInterval(id); document.removeEventListener("visibilitychange", tick); window.removeEventListener("focus", tick); };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setCmdk((o) => !o); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  useEffect(() => { setMore(false); }, [loc.pathname]);

  const inInbox = loc.pathname.startsWith("/inbox");
  const icon = (I: typeof Home) => <I size={16} strokeWidth={1.75} />;

  return (
    <div className="shell">
      <nav className="sidebar" aria-label="Primary">
        <img className="wordmark" src="/assets/hixonops-wordmark.png" alt="HixonOps" />
        <button className="nav-search" onClick={() => setCmdk(true)}>
          <Search size={15} strokeWidth={1.75} /> Search <kbd>⌘K</kbd>
        </button>
        <Item to="/" end icon={icon(Home)} label="Home" />
        <Item to="/inbox" icon={icon(Inbox)} label="Inbox" count={badges?.inboxUnread} />
        <Item to="/todos" icon={icon(CheckSquare)} label="Todos" count={badges?.todosOpen} />
        <Item to="/calendar" icon={icon(CalendarDays)} label="Calendar" />
        <Item to="/leads" icon={icon(UserPlus)} label="Leads" count={badges?.leadsPending} />
        <Item to="/pipeline" icon={icon(Kanban)} label="Pipeline" />
        <Item to="/accounts" icon={icon(Building2)} label="Accounts" />
        <Item to="/systems" icon={icon(Activity)} label="Systems" dot={!!badges?.systemsRed} />
        <Item to="/tickets" icon={icon(Ticket)} label="Tickets" count={badges?.ticketsOpen} />
        <Item to="/quotes" icon={icon(FileText)} label="Quotes" />
        {inInbox && badges?.inboxes && badges.inboxes.length > 1 && (
          <>
            <div className="nav-section">INBOXES</div>
            {badges.inboxes.map((ib) => (
              <NavLink key={ib.id} to={`/inbox?inbox=${ib.id}`} className={() => `nav-sub ${new URLSearchParams(loc.search).get("inbox") === ib.id ? "active" : ""}`}>
                {ib.address.split("@")[0]}@
                {ib.unread > 0 && <span className="count ml-auto mono xs">{ib.unread}</span>}
              </NavLink>
            ))}
          </>
        )}
        <div className="nav-section">WORKSPACE</div>
        <Item to="/partners" icon={icon(Users)} label="Partners" />
        <Item to="/docs" icon={icon(BookOpen)} label="Docs library" />
        {me.role === "admin" && <Item to="/admin" icon={icon(Shield)} label="Admin" />}
        <div className="nav-user">
          <div className="avatar">{me.name[0]?.toUpperCase()}</div>
          <div className="col gap-2">
            <NavLink to="/settings" style={{ color: "var(--text)" }}>{me.name}</NavLink>
            <span className="xs muted">{me.role === "admin" ? "Admin" : "Member"}</span>
          </div>
          <NavLink to="/settings" className="btn-link xs muted ml-auto" title="Settings" aria-label="Settings"><Settings size={14} /></NavLink>
          <button className="btn-link xs muted" title="Sign out" onClick={async () => { await api.post("/auth/logout"); window.location.reload(); }}>Sign out</button>
        </div>
      </nav>

      <main className="main">
        <Outlet />
      </main>

      <nav className="tabbar" aria-label="Primary">
        <NavLink to="/" end className={({ isActive }) => (isActive ? "active" : "")}><Home size={20} strokeWidth={1.75} />Home</NavLink>
        <NavLink to="/inbox" className={({ isActive }) => (isActive ? "active" : "")}>
          <Inbox size={20} strokeWidth={1.75} />Inbox
          {!!badges?.inboxUnread && <span className="badge">{badges.inboxUnread}</span>}
        </NavLink>
        <NavLink to="/todos" className={({ isActive }) => (isActive ? "active" : "")}><CheckSquare size={20} strokeWidth={1.75} />Todos</NavLink>
        <NavLink to="/accounts" className={({ isActive }) => (isActive ? "active" : "")}><Building2 size={20} strokeWidth={1.75} />Accounts</NavLink>
        <a href="#more" onClick={(e) => { e.preventDefault(); setMore(true); }} className={more ? "active" : ""}><MoreHorizontal size={20} strokeWidth={1.75} />More</a>
      </nav>

      {more && (
        <div className="overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setMore(false); }}>
          <div className="modal" style={{ alignSelf: "flex-end" }}>
            <div className="modal-head"><h2>More</h2><button className="btn ghost sm icon ml-auto" onClick={() => setMore(false)}><X size={14} /></button></div>
            <div className="modal-body" style={{ gap: 2 }}>
              {[
                ["/pipeline", "Pipeline"], ["/systems", "Systems"], ["/tickets", "Tickets"], ["/quotes", "Quotes"], ["/partners", "Partners"], ["/docs", "Docs library"],
                ...(me.role === "admin" ? [["/admin", "Admin"]] : []),
              ].map(([to, label]) => (
                <NavLink key={to} to={to} className="nav-item" style={{ minHeight: 44 }}>{label}</NavLink>
              ))}
              <button className="nav-item" style={{ minHeight: 44, border: 0, background: "none", cursor: "pointer" }} onClick={() => { setMore(false); setCmdk(true); }}><Search size={16} /> Search</button>
            </div>
          </div>
        </div>
      )}

      <CommandK open={cmdk} onClose={() => setCmdk(false)} />
      <ToastHost />
    </div>
  );
}
